import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RcDialogWrapperComponent } from '../rc-dialog-wrapper/rc-dialog-wrapper.component';
import { TranslatePipe } from '@app/shared/pipes';
import { SaveConsultationPopinFormValues, ShareRecoPopinOutputData, ShareRecoPopinInputData } from './share-reco-popin';
import { IFormBuilder, IFormGroup } from '@rxweb/types';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { ConsultationFacade } from '@app/store/consultation/consultation.facade';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  filter,
  first,
  map,
  merge,
  Observable,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';
import { customEmailValidator } from '@app/shared/validators/email.validator';
import { Constants, CreatePatientStates } from '@app/shared/utils';
import { VetService } from '@app/core/services';
import { PetOwner } from '@app/core/models';
import { Logger } from '@app/core/services/utils/logger';
import { VetFacade } from '@app/store/vet';
import { RCFieldModule, RCPhoneFieldModule } from '@rc/ui';
import { CheckboxComponent } from '../checkbox/checkbox.component';
import { AsyncPipe } from '@angular/common';
import { getEmailErrorMessage, getInputErrorMessage, getNameErrorMessage } from '@app/shared/utils/static-helpers/form-errors-helper';
import { GtmEventsService } from '@app/core/services/tracking';
import { AllowanceFacade } from '@app/store/allowance';

@Component({
  selector: 'app-share-reco-popin',
  templateUrl: './share-reco-popin.component.html',
  styleUrls: ['./share-reco-popin.component.scss'],
  imports: [RcDialogWrapperComponent, ReactiveFormsModule, RCFieldModule, RCPhoneFieldModule, CheckboxComponent, AsyncPipe, TranslatePipe],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ShareRecoPopinComponent implements OnInit, OnDestroy {
  public readonly Constants = Constants;
  public readonly CreatePatientStates = CreatePatientStates;

  public form: IFormGroup<SaveConsultationPopinFormValues>;
  public state$ = new BehaviorSubject(CreatePatientStates.Initial);
  public scanningMails$ = new BehaviorSubject(false);

  private readonly _destroyed$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ShareRecoPopinInputData,
    private readonly _dialogRef: MatDialogRef<ShareRecoPopinComponent, ShareRecoPopinOutputData>,
    private readonly logger: Logger,
    private readonly vetService: VetService,
    private readonly gtmEventService: GtmEventsService,
    private readonly allowanceFacade: AllowanceFacade,
    private readonly consultationFacade: ConsultationFacade,
    private readonly vetFacade: VetFacade,
  ) {}

  ngOnInit(): void {
    if (this.data.showSaveConsultationForm) {
      const formBuilder: IFormBuilder = new UntypedFormBuilder();
      this.consultationFacade.petOwner$
        .pipe(
          first(),
          withLatestFrom(this.vetFacade.currentClinicCountry$),
          tap(([petOwner, currentClinicCountry]) => {
            this.form = formBuilder.group<SaveConsultationPopinFormValues>({
              givenName: ['', Validators.required],
              familyName: ['', Validators.required],
              email: [petOwner?.email || '', customEmailValidator],
              telephone: [{ phone: '', countryCode: currentClinicCountry }, [Validators.maxLength(18), Validators.minLength(8)]],
              consent: [false, Validators.requiredTrue],
              existingOwner: [null],
            });

            this.listenEmailAndDetectExistingOwner();
          }),
        )
        .subscribe();

      this.allowanceFacade.allowanceParams$
        .pipe(
          take(1),
          tap(({ tool }) => this.gtmEventService.sendCreatePatientPopinView(tool)),
        )
        .subscribe();
    }
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  onSaveConsultation(): void {
    if (this.data.showSaveConsultationForm) {
      if (this.form.valid) {
        this._dialogRef.close({
          type: 'saveConsultation',
          formValues: {
            ...this.form.getRawValue(),
            telephone: this.form.getRawValue().telephone?.phone,
          },
        });
      }
    } else {
      this._dialogRef.close({ type: 'saveConsultation' });
    }
  }

  onBackToHomepage(): void {
    this._dialogRef.close({ type: 'backToHomepage' });
  }

  onClose(): void {
    this._dialogRef.close({ type: 'close' });
  }

  nameErrorMessage(field: string, fieldKeyTrad: string, translationKey: string): string {
    return getNameErrorMessage(this.form.controls[field], fieldKeyTrad, translationKey);
  }

  emailErrorMessage(): string {
    return getEmailErrorMessage(this.form?.controls?.email?.errors, 'form-attribute_email');
  }

  phoneErrorMessage(): string {
    return getInputErrorMessage(this.form?.controls?.telephone?.errors, 'form-attribute_phone');
  }

  private listenEmailAndDetectExistingOwner(): void {
    const petOwnerEmail$: Observable<string> = this.consultationFacade.petOwner$.pipe(
      filter((petOwner) => !!petOwner),
      map((petOwner) => petOwner.email),
    );
    const emitOnEmailValid: Observable<string> = merge(petOwnerEmail$, this.form.controls.email.valueChanges).pipe(
      tap(() => {
        this.form.controls.existingOwner.reset();
        this.form.controls.consent.reset();
        if (this.state$.value === CreatePatientStates.MailExists) {
          this.form.controls.familyName.reset();
          this.form.controls.givenName.reset();
        }
        this.state$.next(CreatePatientStates.Initial);
      }),
      filter(() => this.form.controls.email.valid),
      tap(() => this.scanningMails$.next(true)),
    );
    emitOnEmailValid
      .pipe(
        takeUntil(this._destroyed$),
        switchMap((email) =>
          this.vetService.apiContactsByEmail(email).pipe(
            tap((owners: PetOwner[]) => {
              this.scanningMails$.next(false);

              const owner = owners[0];
              if (owner) {
                this.form.patchValue(
                  { existingOwner: owner, familyName: owner?.familyName, givenName: owner?.givenName },
                  { onlySelf: true, emitEvent: false },
                );
                this.form.controls.givenName.disable();
                this.form.controls.familyName.disable();
                this.state$.next(CreatePatientStates.MailExists);
              } else {
                this.form.controls.givenName.enable();
                this.form.controls.familyName.enable();
                this.form.controls.givenName.setValidators(Constants.NAME_VALIDATORS);
                this.form.controls.familyName.setValidators(Constants.NAME_VALIDATORS);
                this.state$.next(CreatePatientStates.MailFree);
              }
            }),
            catchError((error) => {
              this.scanningMails$.next(false);

              this.state$.next(CreatePatientStates.Error);
              this.logger.errorString('Share Reco Popin: Error while verifying mail:', error);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe();
  }
}
